import {
  Article,
  ChatCircleText,
  MagnifyingGlass,
  MapPin,
  Signpost,
  Storefront,
  X
} from "@phosphor-icons/react";
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { searchTextScore } from "../../lib/search/match";
import { routeDefinitions } from "../../routes";

type SearchGroup = "Pages" | "Spots" | "Resources" | "Gear" | "Community";

type SiteSearchItem = {
  id: string;
  group: SearchGroup;
  title: string;
  detail: string;
  href: string;
  searchText: string;
};

type IndexState = "idle" | "loading" | "ready" | "error";

const groupOrder: SearchGroup[] = ["Pages", "Spots", "Resources", "Gear", "Community"];
const searchablePageIds = new Set([
  "spot-map",
  "weather",
  "resources",
  "gear",
  "community",
  "spot-submit",
  "partners",
  "contact",
  "about",
  "terms",
  "privacy",
  "cookies"
]);
const pageItems: SiteSearchItem[] = routeDefinitions
  .filter((route) => searchablePageIds.has(route.id))
  .map((route) => ({
    id: `page-${route.id}`,
    group: "Pages",
    title: route.label,
    detail: route.description,
    href: route.path,
    searchText: `${route.label} ${route.title} ${route.eyebrow} ${route.description}`
  }));

export function SiteSearch() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const loadPromiseRef = useRef<Promise<void> | null>(null);
  const inputId = useId();
  const listboxId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [items, setItems] = useState<SiteSearchItem[]>(pageItems);
  const [indexState, setIndexState] = useState<IndexState>("idle");
  const [liveSpotsUnavailable, setLiveSpotsUnavailable] = useState(false);
  const visibleResults = useMemo(() => {
    if (!query.trim()) {
      return [];
    }

    const ranked = items
      .map((item) => ({ item, score: searchTextScore(item.searchText, query) }))
      .filter((result) => Number.isFinite(result.score))
      .sort((first, second) => first.score - second.score || first.item.title.localeCompare(second.item.title));

    return groupOrder.flatMap((group) => ranked
      .filter((result) => result.item.group === group)
      .slice(0, 3)
      .map((result) => result.item));
  }, [items, query]);

  useEffect(() => {
    setActiveIndex(0);
    if (!query.trim()) {
      setOpen(false);
    }
  }, [query, visibleResults.length]);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, []);

  function ensureIndex() {
    if (indexState === "ready" || loadPromiseRef.current) {
      return;
    }

    setIndexState("loading");
    const promise = loadSearchItems()
      .then((result) => {
        setItems([...pageItems, ...result.items]);
        setLiveSpotsUnavailable(result.liveSpotsUnavailable);
        setIndexState("ready");
      })
      .catch(() => {
        setIndexState("error");
      })
      .finally(() => {
        loadPromiseRef.current = null;
      });
    loadPromiseRef.current = promise;
  }

  function openSearch(nextQuery = query) {
    ensureIndex();
    if (nextQuery.trim()) {
      setOpen(true);
    }
  }

  function selectResult(result: SiteSearchItem) {
    setOpen(false);
    window.location.assign(result.href);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }

    if (!visibleResults.length) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => (current + 1) % visibleResults.length);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => (current - 1 + visibleResults.length) % visibleResults.length);
      return;
    }

    if (event.key === "Enter" && open) {
      event.preventDefault();
      selectResult(visibleResults[activeIndex] ?? visibleResults[0]);
    }
  }

  const expanded = open && Boolean(query.trim());

  return (
    <div className="site-search" ref={containerRef}>
      <div className="search-box">
        <MagnifyingGlass size={18} aria-hidden="true" />
        <input
          id={inputId}
          type="search"
          value={query}
          placeholder="Search Dahlini"
          autoComplete="off"
          spellCheck={false}
          role="combobox"
          aria-label="Search Dahlini"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={expanded ? listboxId : undefined}
          aria-activedescendant={expanded && visibleResults.length ? `${listboxId}-${activeIndex}` : undefined}
          onFocus={() => openSearch()}
          onChange={(event) => {
            const nextQuery = event.target.value;
            setQuery(nextQuery);
            openSearch(nextQuery);
          }}
          onKeyDown={handleKeyDown}
        />
        {query ? (
          <button
            type="button"
            className="site-search-clear"
            aria-label="Clear site search"
            onClick={(event) => {
              event.preventDefault();
              setQuery("");
              setOpen(false);
            }}
          >
            <X size={15} weight="bold" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {expanded ? (
        <div className="site-search-dropdown" id={listboxId} role="listbox" aria-label="Dahlini search results">
          <div className="site-search-heading">
            <div>
              <strong>Search Dahlini</strong>
              <span>{indexState === "loading" ? "Loading site results" : `${visibleResults.length} results`}</span>
            </div>
            <kbd>Esc</kbd>
          </div>

          {visibleResults.length ? groupOrder.map((group) => {
            const groupResults = visibleResults.filter((result) => result.group === group);
            if (!groupResults.length) {
              return null;
            }

            return (
              <section className="site-search-group" role="group" aria-label={`${group} results`} key={group}>
                <div className="site-search-group-heading">
                  <SearchGroupIcon group={group} />
                  <strong>{group}</strong>
                </div>
                {groupResults.map((result) => {
                  const resultIndex = visibleResults.indexOf(result);
                  return (
                    <a
                      href={result.href}
                      role="option"
                      id={`${listboxId}-${resultIndex}`}
                      aria-selected={resultIndex === activeIndex}
                      className={resultIndex === activeIndex ? "site-search-result active" : "site-search-result"}
                      key={result.id}
                      onPointerMove={() => setActiveIndex(resultIndex)}
                      onClick={() => setOpen(false)}
                    >
                      <strong>{result.title}</strong>
                      <span>{result.detail}</span>
                    </a>
                  );
                })}
              </section>
            );
          }) : indexState === "loading" ? (
            <div className="site-search-loading" role="status" aria-label="Loading site search results">
              <span />
              <span />
              <span />
            </div>
          ) : (
            <div className="site-search-empty">
              <strong>No results for “{query.trim()}”</strong>
              <span>Try a spot, species, guide, gear category, community topic, or page.</span>
            </div>
          )}

          {indexState === "error" ? (
            <div className="site-search-error" role="alert">
              <span>Some site results could not be loaded.</span>
              <button type="button" onClick={ensureIndex}>Retry</button>
            </div>
          ) : liveSpotsUnavailable ? (
            <p className="site-search-note">Live spots are unavailable; other site results are still searchable.</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SearchGroupIcon({ group }: { group: SearchGroup }) {
  if (group === "Spots") {
    return <MapPin size={15} weight="duotone" aria-hidden="true" />;
  }
  if (group === "Resources") {
    return <Article size={15} weight="duotone" aria-hidden="true" />;
  }
  if (group === "Gear") {
    return <Storefront size={15} weight="duotone" aria-hidden="true" />;
  }
  if (group === "Community") {
    return <ChatCircleText size={15} weight="duotone" aria-hidden="true" />;
  }
  return <Signpost size={15} weight="duotone" aria-hidden="true" />;
}

async function loadSearchItems() {
  const [resourceModule, gearModule, communityModule, spotModule] = await Promise.all([
    import("../../data/resources"),
    import("../../data/gear"),
    import("../../data/community"),
    import("../../lib/services/spots")
  ]);

  let liveSpotsUnavailable = false;
  const spotResult = await spotModule.getPublicSpots().catch(() => {
    liveSpotsUnavailable = true;
    return null;
  });
  const spots = spotResult?.status === "live"
    ? spotResult.spots.features.filter((feature) => feature.properties.visibility === "Public")
    : [];

  return {
    liveSpotsUnavailable,
    items: [
      ...spots.map((feature): SiteSearchItem => {
        const spot = feature.properties;
        return {
          id: `spot-${spot.id}`,
          group: "Spots",
          title: spot.name,
          detail: `${spot.waterbody} · ${spot.location}`,
          href: `/?spot=${encodeURIComponent(spot.slug)}`,
          searchText: `${spot.name} ${spot.waterbody} ${spot.location} ${spot.waterType} ${spot.species.join(" ")}`
        };
      }),
      ...resourceModule.resources.map((resource): SiteSearchItem => ({
        id: `resource-${resource.slug}`,
        group: "Resources",
        title: resource.title,
        detail: `${resource.label} · ${resource.summary}`,
        href: `/resources/${resource.slug}`,
        searchText: `${resource.title} ${resource.label} ${resource.summary} ${resource.tags.join(" ")}`
      })),
      ...gearModule.gearCategories.map((category): SiteSearchItem => ({
        id: `gear-${category.slug}`,
        group: "Gear",
        title: category.title,
        detail: category.summary,
        href: `/gear/${category.slug}`,
        searchText: `${category.title} ${category.summary} ${category.plannedFilters.join(" ")}`
      })),
      ...communityModule.communityPosts.map((post): SiteSearchItem => ({
        id: `community-${post.slug}`,
        group: "Community",
        title: post.title,
        detail: `${post.board} · @${post.author}`,
        href: `/community/${post.slug}`,
        searchText: `${post.title} ${post.body} ${post.board} ${post.author} ${post.location} ${post.tags.join(" ")}`
      }))
    ]
  };
}
